/**
 * Compact error state component for Public Recording
 * Displays error icon with inline text, shake entrance animation, and tooltip for details
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';

interface CompactErrorStateProps {
  error: string | null;
}

export const CompactErrorState = ({
  error,
}: CompactErrorStateProps) => {
  const errorMessage = error || 'An error occurred. Please try again.';

  return (
    <motion.div
      key="error"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{ maxHeight: '48px' }}
      className="relative w-full h-full flex items-center justify-center px-3">
      <div className="relative z-10 flex items-center max-w-7xl gap-3">
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <motion.button
              className="flex items-center gap-3 cursor-help focus:outline-none pointer-events-auto"
              type="button"
              aria-label="Error information"
              animate={{ x: [0, -4, 4, -2, 2, 0] }}
              transition={{ duration: 0.4, ease: 'easeOut' }}>
              {/* Error icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 flex items-center justify-center border-2 border-red-200 dark:border-red-800 pointer-events-none">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              </motion.div>

              {/* Inline error text */}
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xs text-red-600 dark:text-red-400 font-medium truncate max-w-xs pointer-events-none">
                {errorMessage}
              </motion.span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="start"
            sideOffset={8}
            avoidCollisions={false}
            className="max-w-sm z-50">
            <div className="space-y-1.5">
              <p className="text-sm break-words whitespace-normal">
                {errorMessage}
              </p>
              <p className="text-xs text-muted-foreground break-all">
                Expected: /public-ai-scribe?appointmentId=XXX&patientId=XXX&locationId=XXX&sectionTag=XXX&integrationKey=XXX&partnerName=XXX
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </motion.div>
  );
};
